(() => {
  if (window.PegGridOrderDataInitialized) return;
  window.PegGridOrderDataInitialized = true;

  const GENERATED_ATTRIBUTE = 'data-peggrid-generated-property';
  const OBSERVER_ATTRIBUTE = 'data-peggrid-order-observed';

  function addHiddenProperty(form, name, value) {
    if (!name || value === undefined || value === null || value === '') return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.value = String(value);
    input.setAttribute(GENERATED_ATTRIBUTE, 'true');
    form.appendChild(input);
  }

  function parseOrderData(productInfo) {
    const dataNode = productInfo.querySelector('[data-peggrid-order-components]');
    if (!dataNode) return null;

    try {
      return JSON.parse(dataNode.textContent || '{}');
    } catch (error) {
      console.error('PegGrid order component data could not be parsed.', error);
      return null;
    }
  }

  function normaliseComponents(data) {
    if (!data || !Array.isArray(data.components)) return [];

    return data.components
      .map((component) => ({
        label: String(component.label || '').trim(),
        quantity: Number.parseInt(component.quantity, 10) || 0,
        url: typeof component.url === 'string' ? component.url : '',
        image: typeof component.image === 'string' ? component.image : '',
        alt: String(component.alt || component.label || '').trim(),
      }))
      .filter((component) => component.label && component.quantity > 0);
  }

  function normaliseQuantity(value) {
    const quantity = Number.parseInt(value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function getSelectedQuantity(productInfo, quantityOverride) {
    if (quantityOverride !== undefined) return normaliseQuantity(quantityOverride);

    const quantityInput = productInfo.querySelector('input[name="quantity"]');
    return normaliseQuantity(quantityInput ? quantityInput.value : 1);
  }

  function setDisplayedQuantity(quantityElement, baseQuantity, selectedQuantity) {
    const totalQuantity = baseQuantity * selectedQuantity;
    quantityElement.dataset.pgBaseQuantity = String(baseQuantity);
    quantityElement.setAttribute('aria-label', `Quantity ${totalQuantity}`);
    quantityElement.textContent = `×\u00a0${totalQuantity}`;
  }

  function createIncludedItem(component, selectedQuantity) {
    const item = document.createElement(component.url ? 'a' : 'div');
    item.className = 'pg-product-included__item';
    if (component.url) item.href = component.url;

    const media = document.createElement('span');
    media.className = 'pg-product-included__media';

    if (component.image) {
      const image = document.createElement('img');
      image.src = component.image;
      image.alt = component.alt;
      image.loading = 'lazy';
      image.width = 220;
      image.height = 220;
      media.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'pg-product-included__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      media.appendChild(placeholder);
    }

    const copy = document.createElement('span');
    copy.className = 'pg-product-included__copy';
    const label = document.createElement('strong');
    label.textContent = component.label;
    copy.appendChild(label);

    const quantity = document.createElement('span');
    quantity.className = 'pg-product-included__quantity';
    setDisplayedQuantity(quantity, component.quantity, selectedQuantity);

    item.append(media, copy, quantity);
    return item;
  }

  function syncIncludedComponents(productInfo, components, selectedQuantity) {
    const included = productInfo.querySelector('[data-pg-product-included]');
    if (!included || components.length === 0) return;

    included.querySelectorAll('.pg-product-included__item').forEach((item) => item.remove());

    const note = included.querySelector('.pg-product-included__note');
    components.forEach((component) => {
      included.insertBefore(createIncludedItem(component, selectedQuantity), note);
    });
  }

  function syncStaticIncludedQuantities(productInfo, selectedQuantity) {
    productInfo.querySelectorAll('[data-pg-product-included] .pg-product-included__quantity').forEach((quantity) => {
      const storedBaseQuantity = Number.parseInt(quantity.dataset.pgBaseQuantity, 10);
      const textBaseQuantity = Number.parseInt(quantity.textContent.replace(/[^0-9]/g, ''), 10);
      const baseQuantity = storedBaseQuantity || textBaseQuantity || 1;
      setDisplayedQuantity(quantity, baseQuantity, selectedQuantity);
    });
  }

  function openIncludedOnInitialLoad(productInfo) {
    if (productInfo.dataset.peggridIncludedInitialised === 'true') return;

    const included = productInfo.querySelector('[data-pg-product-included]');
    const details = included ? included.closest('details') : null;
    if (!details) return;

    details.open = true;
    details.setAttribute('aria-expanded', 'true');
    productInfo.dataset.peggridIncludedInitialised = 'true';
  }

  function syncProductInfo(productInfo, quantityOverride) {
    if (!productInfo) return;

    openIncludedOnInitialLoad(productInfo);
    observeProductInfo(productInfo);

    const selectedQuantity = getSelectedQuantity(productInfo, quantityOverride);
    const form = productInfo.querySelector('form[is="product-form"]');
    if (!form) {
      syncStaticIncludedQuantities(productInfo, selectedQuantity);
      return;
    }

    form.querySelectorAll(`[${GENERATED_ATTRIBUTE}]`).forEach((input) => input.remove());

    const data = parseOrderData(productInfo);
    const components = normaliseComponents(data);
    if (components.length === 0) {
      syncStaticIncludedQuantities(productInfo, selectedQuantity);
      return;
    }

    syncIncludedComponents(productInfo, components, selectedQuantity);

    const summary = components
      .map((component) => `${component.label} × ${component.quantity}`)
      .join(' · ');

    addHiddenProperty(form, 'Set contents (per set)', summary);
    addHiddenProperty(form, '_peggrid_configuration', data.configuration || '');

    components.forEach((component, index) => {
      const position = String(index + 1).padStart(2, '0');
      addHiddenProperty(form, `_pack_${position}`, `${component.label}|${component.quantity}`);
    });
  }

  let syncFrame;
  let pendingQuantity;

  function scheduleSync(quantityOverride) {
    if (quantityOverride !== undefined) {
      pendingQuantity = normaliseQuantity(quantityOverride);
    }

    window.cancelAnimationFrame(syncFrame);
    syncFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const quantity = pendingQuantity;
        pendingQuantity = undefined;
        syncAll(quantity);
      });
    });
  }

  function observeProductInfo(productInfo) {
    if (productInfo.hasAttribute(OBSERVER_ATTRIBUTE)) return;
    productInfo.setAttribute(OBSERVER_ATTRIBUTE, 'true');

    const observer = new MutationObserver((mutations) => {
      const orderDataChanged = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches('[data-peggrid-order-components]') ||
              node.querySelector('[data-peggrid-order-components]'))
        )
      );

      if (orderDataChanged) scheduleSync();
    });

    observer.observe(productInfo, { childList: true, subtree: true });
  }

  function getQuantityInputFromEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    if (target.matches('input[name="quantity"]')) return target;

    const quantityButton = target.closest('button[name="plus"], button[name="minus"]');
    if (!quantityButton) return null;

    const quantityControl = quantityButton.closest('quantity-input') || quantityButton.parentElement;
    return quantityControl ? quantityControl.querySelector('input[name="quantity"]') : null;
  }

  function handleQuantityInteraction(event) {
    const quantityInput = getQuantityInputFromEvent(event);
    if (!quantityInput) return;

    if (event.type === 'input' || event.type === 'change') {
      syncAll(quantityInput.value);
      return;
    }

    scheduleSync(quantityInput.value);
  }

  function syncAll(quantityOverride) {
    document.querySelectorAll('product-info').forEach((productInfo) => {
      syncProductInfo(productInfo, quantityOverride);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll, { once: true });
  } else {
    syncAll();
  }

  document.addEventListener('input', handleQuantityInteraction);
  document.addEventListener('change', handleQuantityInteraction);
  document.addEventListener('click', handleQuantityInteraction);
  document.addEventListener('variant:changed', () => scheduleSync());
  document.addEventListener('shopify:section:load', () => scheduleSync());
})();